/**
 * SchemaEditor — éditeur contrôlé du schéma d'un brouillon (F2-Forms-1c-ii).
 *
 * `value` (EditorSchema) + `onChange` : toute édition passe par les opérations
 * immuables de schemaModel. Erreurs inline dérivées de `value` (format/unicité de
 * clé, libellés bilingues requis). Clés verrouillées (`persistedUids`) = éléments
 * déjà persistés (non éditables, décision PO §2). `disabled` = lecture seule ; textes sélectionnables au clavier pour récupération.
 * Réorg ↑/↓ (aucun DnD) ; clés React = `_uid` (focus conservé). Aucun aperçu,
 * aucune publication (1c-iii).
 */
import { BLOCK_REF_SYNTAX, collectBlockRefs } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import * as M from "./schemaModel";

type Props = {
  readonly value: M.EditorSchema;
  readonly onChange: (next: M.EditorSchema) => void;
  readonly persistedUids: ReadonlySet<string>;
  readonly defaultLang: "fr" | "en";
  readonly disabled?: boolean;
  readonly errors?: Readonly<Record<string, string>>;
};

function keyCounts(schema: M.EditorSchema): Map<string, number> {
  const m = new Map<string, number>();
  for (const k of M.collectKeys(schema)) m.set(k, 0);
  const bump = (k: string) => m.set(k, (m.get(k) ?? 0) + 1);
  for (const s of schema.sections) {
    bump(s.key);
    for (const f of s.fields) {
      bump(f.key);
      for (const o of f.options ?? []) bump(o.value);
    }
  }
  return m;
}

const inputCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500 read-only:bg-slate-50";

export function SchemaEditor({
  value,
  onChange,
  persistedUids,
  defaultLang,
  disabled = false,
  errors = {},
}: Props) {
  const { t } = useTranslation();
  const [confirmSection, setConfirmSection] = useState<number | null>(null);
  const counts = keyCounts(value);

  const keyError = (key: string): string | null => {
    if (!M.STABLE_KEY_RE.test(key)) return t("form_templates.editor.errors.key_format");
    if ((counts.get(key) ?? 0) > 1) return t("form_templates.editor.errors.key_duplicate");
    return null;
  };

  return (
    <div className="space-y-5">
      {value.sections.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
          {t("form_templates.editor.no_sections")}
        </p>
      )}

      {value.sections.map((section, si) => (
        <SectionCard
          key={section._uid}
          section={section}
          errors={errors}
          si={si}
          total={value.sections.length}
          disabled={disabled}
          defaultLang={defaultLang}
          persistedUids={persistedUids}
          keyError={keyError}
          onChange={onChange}
          value={value}
          onRequestDelete={() =>
            section.fields.length > 0 ? setConfirmSection(si) : onChange(M.removeSection(value, si))
          }
        />
      ))}

      {!disabled && (
        <button
          type="button"
          onClick={() => onChange(M.addSection(value, defaultLang))}
          className="border-brand-300 bg-brand-50/50 text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg border border-dashed px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <span aria-hidden="true">+</span> {t("form_templates.editor.add_section")}
        </button>
      )}

      {confirmSection !== null && (
        <ConfirmDialog
          title={t("form_templates.editor.delete_section_title")}
          body={t("form_templates.editor.delete_section_confirm", {
            count: value.sections[confirmSection]?.fields.length ?? 0,
          })}
          confirmLabel={t("form_templates.editor.delete")}
          onCancel={() => setConfirmSection(null)}
          onConfirm={() => {
            onChange(M.removeSection(value, confirmSection));
            setConfirmSection(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SectionCard(props: {
  section: M.EditorSection;
  errors: Readonly<Record<string, string>>;
  si: number;
  total: number;
  disabled: boolean;
  defaultLang: "fr" | "en";
  persistedUids: ReadonlySet<string>;
  keyError: (key: string) => string | null;
  value: M.EditorSchema;
  onChange: (next: M.EditorSchema) => void;
  onRequestDelete: () => void;
}) {
  const { section, si, total, disabled, defaultLang, persistedUids, keyError, value, onChange } =
    props;
  const { t } = useTranslation();
  const sectionName = section.label[defaultLang] || section.key || `#${si + 1}`;

  return (
    <fieldset className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("form_templates.editor.section")} {si + 1}
      </legend>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-3">
          <BilingualLabelInput
            idBase={`sec-${section._uid}-label`}
            legend={t("form_templates.editor.section_label")}
            value={section.label}
            disabled={disabled}
            onChange={(label) => onChange(M.updateSection(value, si, { label }))}
          />
          <KeyField
            id={`sec-${section._uid}-key`}
            value={section.key}
            locked={disabled || persistedUids.has(section._uid)}
            error={keyError(section.key)}
            onChange={(key) => onChange(M.updateSection(value, si, { key }))}
          />
        </div>
        {!disabled && (
          <div className="flex shrink-0 items-center gap-1">
            <Reorder
              name={sectionName}
              upDisabled={si === 0}
              downDisabled={si === total - 1}
              onUp={() => onChange(M.moveSection(value, si, -1))}
              onDown={() => onChange(M.moveSection(value, si, 1))}
            />
            <DeleteButton
              label={t("form_templates.editor.delete_section_aria", { name: sectionName })}
              onClick={props.onRequestDelete}
            />
          </div>
        )}
      </div>

      {value.version_metadata.purpose === "business_spec" && (
        <div className="mt-4 text-sm">
          {section.coverage_choice_key ? (
            <p className="text-slate-600">{t("request_flow.coverage_configured")}</p>
          ) : (
            <button
              type="button"
              disabled={disabled}
              className="focus-visible:outline-brand-500 rounded border border-slate-300 px-3 py-2 focus-visible:outline-2 disabled:opacity-50"
              onClick={() => onChange(M.enableCoverageChoice(value, si))}
            >
              {t("request_flow.coverage_enable")}
            </button>
          )}
        </div>
      )}

      {/* Contenu — 9.BJ.2. AVANT les champs, comme au rendu : on lit, puis on
          répond. Un éditeur qui les présenterait dans l'autre ordre ferait
          composer un document qu'on ne reconnaîtrait pas à l'écran. */}
      <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
        {section.blocks.map((bloc, bi) => (
          <BlockCard
            key={bloc._uid}
            bloc={bloc}
            si={si}
            bi={bi}
            total={section.blocks.length}
            disabled={disabled}
            persistedUids={persistedUids}
            keyError={keyError}
            value={value}
            onChange={onChange}
          />
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange(M.addBlock(value, si, defaultLang))}
            className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <span aria-hidden="true">+</span> {t("form_templates.editor.add_block")}
          </button>
        )}
      </div>

      {/* Champs */}
      <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
        {section.fields.length === 0 && (
          <p className="text-sm text-slate-500">{t("form_templates.editor.no_fields")}</p>
        )}
        {section.fields.map((field, fi) => (
          <FieldCard
            key={field._uid}
            field={field}
            errors={props.errors}
            si={si}
            fi={fi}
            total={section.fields.length}
            disabled={disabled}
            defaultLang={defaultLang}
            persistedUids={persistedUids}
            keyError={keyError}
            value={value}
            onChange={onChange}
          />
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange(M.addField(value, si, defaultLang))}
            className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <span aria-hidden="true">+</span> {t("form_templates.editor.add_field")}
          </button>
        )}
      </div>
    </fieldset>
  );
}

/**
 * L'édition d'un bloc de contenu — chunk 9.BJ.2.
 *
 * Deux zones de texte, une par langue, et rien d'autre : pas de type, pas de
 * « requis », pas de rôle, pas d'options. C'est ce qui distingue un bloc d'un
 * champ, et l'éditeur doit le rendre évident sans qu'on lise la documentation.
 */
function BlockCard(props: {
  bloc: M.EditorBlock;
  si: number;
  bi: number;
  total: number;
  disabled: boolean;
  persistedUids: ReadonlySet<string>;
  keyError: (k: string) => string | null;
  value: M.EditorSchema;
  onChange: (next: M.EditorSchema) => void;
}) {
  const { bloc, si, bi, total, disabled, persistedUids, keyError, value, onChange } = props;

  // 9.BJ.7 — un bloc peut citer un champ : `{{cle}}`. Une clé qui ne désigne
  // aucun champ est refusée à la publication ; la signaler ICI évite de
  // l'apprendre au moment de publier, un écran plus loin.
  const clesChamps = new Set(value.sections.flatMap((sec) => sec.fields.map((f) => f.key)));
  const refsMortes = [
    ...new Set([...collectBlockRefs(bloc.text.fr), ...collectBlockRefs(bloc.text.en)]),
  ].filter((cle) => !clesChamps.has(cle));
  const { t } = useTranslation();
  const nom = bloc.text.fr || bloc.text.en || bloc.key || `${bi + 1}`;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("form_templates.editor.block")} {bi + 1}
        </p>
        {!disabled && (
          <div className="flex shrink-0 items-center gap-1">
            <Reorder
              name={nom}
              upDisabled={bi === 0}
              downDisabled={bi === total - 1}
              onUp={() => onChange(M.moveBlock(value, si, bi, -1))}
              onDown={() => onChange(M.moveBlock(value, si, bi, 1))}
            />
            <DeleteButton
              label={t("form_templates.editor.delete_block_aria", { name: nom })}
              onClick={() => onChange(M.removeBlock(value, si, bi))}
            />
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        {(["fr", "en"] as const).map((lng) => {
          const id = `bloc-${bloc._uid}-${lng}`;
          const vide = bloc.text[lng].trim() === "";
          return (
            <div key={lng}>
              <label htmlFor={id} className="text-xs uppercase text-slate-400">
                {lng}
              </label>
              <textarea
                id={id}
                rows={6}
                value={bloc.text[lng]}
                readOnly={disabled}
                maxLength={20000}
                spellCheck
                aria-invalid={!disabled && vide ? true : undefined}
                aria-describedby={!disabled && vide ? `${id}-err` : `${id}-aide`}
                onChange={(e) =>
                  onChange(
                    M.updateBlock(value, si, bi, { text: { ...bloc.text, [lng]: e.target.value } }),
                  )
                }
                className="focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm read-only:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
              />
              {!disabled && vide ? (
                <p id={`${id}-err`} role="alert" className="mt-0.5 text-xs text-red-600">
                  {t("form_templates.editor.errors.block_text_required")}
                </p>
              ) : (
                <p id={`${id}-aide`} className="mt-0.5 text-xs text-slate-500">
                  {t("form_templates.editor.block_help", { syntax: BLOCK_REF_SYNTAX })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!disabled && refsMortes.length > 0 && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {t("form_templates.editor.block_refs_unknown", {
            refs: refsMortes.map((k) => `{{${k}}}`).join(", "),
          })}
        </p>
      )}

      <div className="mt-3">
        <KeyField
          id={`bloc-${bloc._uid}-key`}
          value={bloc.key}
          locked={disabled || persistedUids.has(bloc._uid)}
          error={keyError(bloc.key)}
          onChange={(key) => onChange(M.updateBlock(value, si, bi, { key }))}
        />
      </div>
    </div>
  );
}

function FieldCard(props: {
  field: M.EditorField;
  errors: Readonly<Record<string, string>>;
  si: number;
  fi: number;
  total: number;
  disabled: boolean;
  defaultLang: "fr" | "en";
  persistedUids: ReadonlySet<string>;
  keyError: (key: string) => string | null;
  value: M.EditorSchema;
  onChange: (next: M.EditorSchema) => void;
}) {
  const { field, si, fi, total, disabled, defaultLang, persistedUids, keyError, value, onChange } =
    props;
  const { t } = useTranslation();
  const fieldName = field.label[defaultLang] || field.key || `#${fi + 1}`;

  return (
    <fieldset className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <legend className="px-1 text-xs font-medium text-slate-500">
        {t("form_templates.editor.field")} {fi + 1}
      </legend>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-3">
          {/* Type */}
          <div>
            <label
              htmlFor={`f-${field._uid}-type`}
              className="block text-sm font-medium text-slate-700"
            >
              {t("form_templates.editor.field_type")}
            </label>
            <select
              id={`f-${field._uid}-type`}
              value={field.type}
              disabled={disabled}
              onChange={(e) =>
                onChange(M.changeFieldType(value, si, fi, e.target.value as M.FieldType))
              }
              className={inputCls}
            >
              {M.FIELD_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {t(`form_templates.editor.types.${ft}`)}
                </option>
              ))}
            </select>
          </div>

          <BilingualLabelInput
            idBase={`f-${field._uid}-label`}
            legend={t("form_templates.editor.field_label")}
            value={field.label}
            disabled={disabled}
            onChange={(label) => onChange(M.updateField(value, si, fi, { label }))}
          />

          {(["help", "placeholder"] as const).map((property) => (
            <fieldset key={property} className="min-w-0">
              <legend className="text-sm font-medium text-slate-700">
                {t(`form_templates.editor.field_${property}`)}
              </legend>
              <div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(["fr", "en"] as const).map((language) => (
                  <div key={language}>
                    <label
                      htmlFor={`f-${field._uid}-${property}-${language}`}
                      className="text-xs text-slate-500"
                    >
                      {t(`form_templates.editor.field_${property}`)} ({language.toUpperCase()})
                    </label>
                    <textarea
                      id={`f-${field._uid}-${property}-${language}`}
                      value={field[property]?.[language] ?? ""}
                      readOnly={disabled}
                      aria-invalid={
                        props.errors[`f-${field._uid}-${property}-${language}`] ? true : undefined
                      }
                      aria-describedby={
                        props.errors[`f-${field._uid}-${property}-${language}`]
                          ? `f-${field._uid}-${property}-${language}-error`
                          : undefined
                      }
                      maxLength={300}
                      rows={2}
                      onChange={(event) =>
                        onChange(
                          M.updateField(value, si, fi, {
                            [property]: {
                              fr: field[property]?.fr ?? "",
                              en: field[property]?.en ?? "",
                              [language]: event.target.value,
                            },
                          }),
                        )
                      }
                      className={inputCls}
                    />
                    {props.errors[`f-${field._uid}-${property}-${language}`] && (
                      <p
                        id={`f-${field._uid}-${property}-${language}-error`}
                        className="mt-1 text-xs text-red-600"
                      >
                        {props.errors[`f-${field._uid}-${property}-${language}`]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          ))}

          <KeyField
            id={`f-${field._uid}-key`}
            value={field.key}
            locked={disabled || persistedUids.has(field._uid)}
            error={keyError(field.key)}
            onChange={(key) => onChange(M.updateField(value, si, fi, { key }))}
          />

          {/* required */}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={field.required}
              disabled={disabled}
              onChange={(e) =>
                onChange(M.updateField(value, si, fi, { required: e.target.checked }))
              }
              className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 rounded border-slate-300 focus-visible:ring-2"
            />
            {t("form_templates.editor.required")}
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={field.scenario_parameter === true}
              disabled={
                disabled ||
                !["number", "select", "multi_select", "checkbox"].includes(field.type) ||
                Boolean(field.role || field.prefill_from)
              }
              onChange={(e) =>
                onChange(M.updateField(value, si, fi, { scenario_parameter: e.target.checked }))
              }
              className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 rounded border-slate-300 focus-visible:ring-2"
            />
            {t("scenarios.parameter")}
          </label>
          <p className="text-xs text-slate-600">{t("scenarios.parameter_help")}</p>

          {/* Rôle de mapping (1e-B) — uniquement si des rôles sont compatibles
              avec le type courant. « Aucun rôle » = valeur vide. Le rôle est
              retiré automatiquement si le type devient incompatible (changeFieldType). */}
          {M.rolesForType(field.type).length > 0 && (
            <div>
              <label
                htmlFor={`f-${field._uid}-role`}
                className="block text-sm font-medium text-slate-700"
              >
                {t("form_templates.editor.role_label")}
              </label>
              <select
                id={`f-${field._uid}-role`}
                value={field.role ?? ""}
                disabled={disabled}
                onChange={(e) =>
                  onChange(
                    M.updateField(value, si, fi, {
                      role: (e.target.value || undefined) as M.FieldRole | undefined,
                    }),
                  )
                }
                className={inputCls}
              >
                <option value="">{t("form_templates.editor.role_none")}</option>
                {M.rolesForType(field.type).map((r) => (
                  <option key={r} value={r}>
                    {t(`form_templates.editor.roles.${r}`)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">{t("form_templates.editor.role_help")}</p>
            </div>
          )}

          {/* 9.BJ.3 — d'où le serveur amorce la valeur. Sens INVERSE du rôle
              ci-dessus : le rôle dit ce que la réponse alimente, la source dit
              ce qui remplit le champ à l'ouverture. Deux attributs, parce que
              les confondre ferait changer la matérialisation en déclarant un
              amorçage. */}
          {M.prefillSourcesForType(field.type).length > 0 && (
            <div>
              <label
                htmlFor={`f-${field._uid}-prefill`}
                className="block text-sm font-medium text-slate-700"
              >
                {t("form_templates.editor.prefill_label")}
              </label>
              <select
                id={`f-${field._uid}-prefill`}
                value={field.prefill_from ?? ""}
                disabled={disabled}
                onChange={(e) =>
                  onChange(
                    M.updateField(value, si, fi, {
                      prefill_from: (e.target.value || undefined) as M.PrefillSource | undefined,
                    }),
                  )
                }
                className={inputCls}
              >
                <option value="">{t("form_templates.editor.prefill_none")}</option>
                {M.prefillSourcesForType(field.type).map((src) => (
                  <option key={src} value={src}>
                    {t(`form_templates.editor.prefill_sources.${src}`)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                {t("form_templates.editor.prefill_help")}
              </p>
            </div>
          )}

          {/* Options — select/multi_select uniquement (aucun contrôle sans effet) */}
          {M.typeNeedsOptions(field.type) && (
            <OptionsEditor
              field={field}
              si={si}
              fi={fi}
              disabled={disabled}
              defaultLang={defaultLang}
              persistedUids={persistedUids}
              keyError={keyError}
              value={value}
              onChange={onChange}
            />
          )}

          {/* Validation — text/textarea uniquement */}
          {M.typeSupportsValidation(field.type) && (
            <ValidationEditor
              field={field}
              si={si}
              fi={fi}
              disabled={disabled}
              value={value}
              onChange={onChange}
            />
          )}
        </div>

        {!disabled && (
          <div className="flex shrink-0 items-center gap-1">
            <Reorder
              name={fieldName}
              upDisabled={fi === 0}
              downDisabled={fi === total - 1}
              onUp={() => onChange(M.moveField(value, si, fi, -1))}
              onDown={() => onChange(M.moveField(value, si, fi, 1))}
            />
            <DeleteButton
              label={t("form_templates.editor.delete_field_aria", { name: fieldName })}
              onClick={() => onChange(M.removeField(value, si, fi))}
            />
          </div>
        )}
      </div>
    </fieldset>
  );
}

function OptionsEditor(props: {
  field: M.EditorField;
  si: number;
  fi: number;
  disabled: boolean;
  defaultLang: "fr" | "en";
  persistedUids: ReadonlySet<string>;
  keyError: (key: string) => string | null;
  value: M.EditorSchema;
  onChange: (next: M.EditorSchema) => void;
}) {
  const { field, si, fi, disabled, defaultLang, persistedUids, keyError, value, onChange } = props;
  const { t } = useTranslation();
  const options = field.options ?? [];

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("form_templates.editor.options")}
      </p>
      {options.length === 0 && (
        <p className="mt-1 text-xs text-slate-500">{t("form_templates.editor.options_empty")}</p>
      )}
      <ul className="mt-2 space-y-2">
        {options.map((opt, oi) => (
          <li key={opt._uid} className="flex items-start gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              <BilingualLabelInput
                idBase={`o-${opt._uid}-label`}
                legend={t("form_templates.editor.option_label")}
                value={opt.label}
                disabled={disabled}
                compact
                onChange={(label) => onChange(M.updateOption(value, si, fi, oi, { label }))}
              />
              <KeyField
                id={`o-${opt._uid}-val`}
                value={opt.value}
                locked={disabled || persistedUids.has(opt._uid)}
                error={keyError(opt.value)}
                labelKey="form_templates.editor.option_value"
                onChange={(val) => onChange(M.updateOption(value, si, fi, oi, { value: val }))}
              />
            </div>
            {!disabled && (
              <div className="flex shrink-0 items-center gap-1">
                <Reorder
                  name={opt.label[defaultLang] || opt.value}
                  upDisabled={oi === 0}
                  downDisabled={oi === options.length - 1}
                  onUp={() => onChange(M.moveOption(value, si, fi, oi, -1))}
                  onDown={() => onChange(M.moveOption(value, si, fi, oi, 1))}
                />
                <DeleteButton
                  label={t("form_templates.editor.delete_option_aria", {
                    name: opt.label[defaultLang] || opt.value,
                  })}
                  onClick={() => onChange(M.removeOption(value, si, fi, oi))}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
      {!disabled && (
        <button
          type="button"
          onClick={() => onChange(M.addOption(value, si, fi, defaultLang))}
          className="focus-visible:ring-brand-500 mt-2 inline-flex touch-manipulation items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <span aria-hidden="true">+</span> {t("form_templates.editor.add_option")}
        </button>
      )}
    </div>
  );
}

function ValidationEditor(props: {
  field: M.EditorField;
  si: number;
  fi: number;
  disabled: boolean;
  value: M.EditorSchema;
  onChange: (next: M.EditorSchema) => void;
}) {
  const { field, si, fi, disabled, value, onChange } = props;
  const { t } = useTranslation();
  const v = field.validation ?? {};
  const setV = (patch: Partial<M.EditorValidation>) =>
    onChange(M.updateField(value, si, fi, { validation: { ...v, ...patch } }));
  const numOrUndef = (s: string): number | undefined => (s.trim() === "" ? undefined : Number(s));

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("form_templates.editor.validation")}
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-600">
          {t("form_templates.editor.min_length")}
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={v.min_length ?? ""}
            readOnly={disabled}
            onChange={(e) => setV({ min_length: numOrUndef(e.target.value) })}
            className={inputCls}
          />
        </label>
        <label className="text-xs text-slate-600">
          {t("form_templates.editor.max_length")}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={v.max_length ?? ""}
            readOnly={disabled}
            onChange={(e) => setV({ max_length: numOrUndef(e.target.value) })}
            className={inputCls}
          />
        </label>
      </div>
      <label className="mt-2 block text-xs text-slate-600">
        {t("form_templates.editor.regex")}
        <input
          type="text"
          spellCheck={false}
          autoComplete="off"
          value={v.regex ?? ""}
          readOnly={disabled}
          onChange={(e) => setV({ regex: e.target.value === "" ? undefined : e.target.value })}
          className={inputCls}
        />
      </label>
    </div>
  );
}

function BilingualLabelInput(props: {
  idBase: string;
  legend: string;
  value: M.Bilingual;
  disabled: boolean;
  compact?: boolean;
  onChange: (next: M.Bilingual) => void;
}) {
  const { idBase, legend, value, disabled, compact, onChange } = props;
  const { t } = useTranslation();
  const frEmpty = value.fr.trim() === "";
  const enEmpty = value.en.trim() === "";
  return (
    <div>
      <span className="block text-sm font-medium text-slate-700">{legend}</span>
      <div className={compact ? "grid grid-cols-2 gap-2" : "mt-1 grid grid-cols-2 gap-3"}>
        {(["fr", "en"] as const).map((lng) => {
          const empty = lng === "fr" ? frEmpty : enEmpty;
          return (
            <div key={lng}>
              <label htmlFor={`${idBase}-${lng}`} className="text-xs uppercase text-slate-400">
                {lng}
              </label>
              <input
                id={`${idBase}-${lng}`}
                type="text"
                value={value[lng]}
                readOnly={disabled}
                maxLength={300}
                aria-invalid={!disabled && empty ? true : undefined}
                aria-describedby={!disabled && empty ? `${idBase}-${lng}-err` : undefined}
                onChange={(e) => onChange({ ...value, [lng]: e.target.value })}
                className={inputCls}
              />
              {!disabled && empty && (
                <p id={`${idBase}-${lng}-err`} role="alert" className="mt-0.5 text-xs text-red-600">
                  {t("form_templates.editor.errors.label_required")}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KeyField(props: {
  id: string;
  value: string;
  locked: boolean;
  error: string | null;
  labelKey?: string;
  onChange: (next: string) => void;
}) {
  const { id, value, locked, error, labelKey, onChange } = props;
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-500">
        {t(labelKey ?? "form_templates.editor.key")}
        {locked && <span className="ml-1 text-slate-400">🔒</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        readOnly={locked}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={!locked && error ? true : undefined}
        aria-describedby={!locked && error ? `${id}-err` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} font-mono ${locked ? "bg-slate-100 text-slate-500" : ""}`}
      />
      {!locked && error && (
        <p id={`${id}-err`} role="alert" className="mt-0.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function Reorder(props: {
  name: string;
  upDisabled: boolean;
  downDisabled: boolean;
  onUp: () => void;
  onDown: () => void;
}) {
  const { t } = useTranslation();
  const btn =
    "grid h-7 w-7 place-items-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 touch-manipulation";
  return (
    <>
      <button
        type="button"
        onClick={props.onUp}
        disabled={props.upDisabled}
        aria-label={t("form_templates.editor.move_up_aria", { name: props.name })}
        className={btn}
      >
        <span aria-hidden="true">↑</span>
      </button>
      <button
        type="button"
        onClick={props.onDown}
        disabled={props.downDisabled}
        aria-label={t("form_templates.editor.move_down_aria", { name: props.name })}
        className={btn}
      >
        <span aria-hidden="true">↓</span>
      </button>
    </>
  );
}

function DeleteButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid h-7 w-7 touch-manipulation place-items-center rounded-md border border-slate-300 bg-white text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
    >
      <span aria-hidden="true">✕</span>
    </button>
  );
}

function ConfirmDialog(props: {
  title: string;
  body: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={props.onCancel}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-5 shadow-xl ring-1 ring-slate-200">
        <h2 className="text-base font-semibold text-slate-900">{props.title}</h2>
        <p className="mt-2 text-sm text-slate-700">{props.body}</p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={props.onCancel}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            className="touch-manipulation rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
